import { useRequest } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Loading, MessagePlugin } from 'tdesign-react';

import Empty from '@/assets/svg/empty3.png';
import { Star3 } from '@/components/icon';
import { StoryLayout } from '@/interface';
import { AIGC_IMAGE_DIMENTION, AIGC_IMAGE_PROMPT_LENGTH, AiImageModelType, AiImageReferenceNumber, AiImageReferenceType, AiImageReq, AiImageResolution, AiPrompt, AiType, DEFAULT_AI_IMAGE_MODEL, GetAIResourceRsp, Mode, ResourceHistory, Status, UrlInfo, UserLevel } from '@/interface/aigc';
import { aIDrawing, getAIResource } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { useTypedSelector } from '@/store';
import { getUnicodeLength } from '@/utils/getUnicodeLength';

import { AigcImageDialogProps } from '../AigcImageDialog';
import AigcImageHistory from './history';
import styles from './index.module.less';
import AigcImageInfo, { AigcImageInfoRef } from './info';
import { getUsageExceedMessage } from './util';

interface AigcImageProps {
  visible: boolean;
  data: AigcImageDialogProps['data'];
  isHorizontal: boolean;
  getSrc: (src: UrlInfo) => void;
  getPrompt?: (prompt: string) => void;
  pagination: {
    page: number;
    size: number;
    total: number;
  };
  onPageChange: (pageInfo: {
    page: number,
    size: number,
    total: number,
  }) => void;
}

const DEFAULT_AI_PROMPT: AiPrompt = {
  prompt: '',
  image: {},
  referenceType: AiImageReferenceType.AUTO,
  cw: AiImageReferenceNumber[AiImageReferenceType.CW],
  sw: AiImageReferenceNumber[AiImageReferenceType.SW],
};

const FAILED_MESSAGE = 'AI绘图失败，请点击重试';
const TIMEOUT = 60000 * 6;
const AIGC_IMAGE_COUNT = 4;

const AigcImageComponent: React.FC<AigcImageProps> = (props) => {
  const { visible, data, isHorizontal, pagination, onPageChange, getSrc, getPrompt } = props;
  const [histories, setHistories] = useState<ResourceHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<UserLevel>();
  const { storyInfo } = useTypedSelector(store => store.story);

  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const aigcImageInfoRef = useRef<AigcImageInfoRef>(null);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      getImages();
    } else {
      cleanup({ cleanHistories: false });
    }

    return () => cleanup({ cleanHistories: false });
  }, [visible, pagination.page, pagination.size]);

  // 判断参考类型
  const getReferType = (model: AiImageModelType, imageUrl: string, curRefer: AiImageReferenceType) => {
    // 没有参考图按默认类型
    if (!imageUrl) return AiImageReferenceType.DEFAULT;

    switch (model) {
      case AiImageModelType.AI_IMAGE_JIMENG_31:
        // 即梦3.1模型：不支持参考图
        return AiImageReferenceType.DEFAULT;
      // case AiImageModelType.AI_IMAGE_FANGZHOU_40:
      //   // 方舟4.0模型：仅支持智能参考
      //   return AiImageReferenceType.AUTO;
      default:
        // 其他模型：保持当前参考类型
        return curRefer;
    }
  };

  const generate = async () => {
    aigcImageInfoRef.current?.submitForm();
    const imageInfo = aigcImageInfoRef.current?.getImageInfo();
    const { text, style, model = DEFAULT_AI_IMAGE_MODEL, resolution = AiImageResolution['1K'].value } = imageInfo || {};
    const {
      prompt = '',
      image,
      referenceType = AiImageReferenceType.AUTO,
      sw = AiImageReferenceNumber[AiImageReferenceType.SW],
      cw = AiImageReferenceNumber[AiImageReferenceType.CW],
    }: AiPrompt = text || DEFAULT_AI_PROMPT;

    let errorMessage = null;
    if (prompt?.trim().length === 0) {
      errorMessage = '请填写画面描述';
    } else if (getUnicodeLength(prompt) > AIGC_IMAGE_PROMPT_LENGTH) {
      errorMessage = `最多输入${AIGC_IMAGE_PROMPT_LENGTH}字`;
    }
    if (errorMessage) {
      MessagePlugin.error({ content: errorMessage });
      return;
    }

    const base = isHorizontal
      ? AIGC_IMAGE_DIMENTION[StoryLayout.LAYOUT_HORIZONTAL]
      : AIGC_IMAGE_DIMENTION[StoryLayout.LAYOUT_VERTICAL];
    const imageUrl = model === AiImageModelType.AI_IMAGE_JIMENG_31 ? '' : (image?.url?.url || '');
    const referType = getReferType(model, imageUrl, referenceType);

    const postData: AiImageReq = {
      text: prompt,
      baseWidth: base.baseWidth * resolution,
      baseHeight: base.baseHeight * resolution,
      mode: Mode.TXT2IMG,
      imageUrl,
      queryId: '',
      model,
      referenceType: referType,
      style,
      mjReq: {
        ...(referType === AiImageReferenceType.CW && imageUrl && {
          crefInfo: { imageUrl, cw },
        }),
        ...(referType === AiImageReferenceType.SW && imageUrl && {
          srefInfo: { imageUrl, sw },
        }),
      },
    };

    await generateImages(postData);
  };

  const generateImages = async (postData: AiImageReq) => {
    onPageChange({ ...pagination, page: 1 });

    const { code, data, message } = await aIDrawing(postData);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || FAILED_MESSAGE,
      });
      if (code === CgiErrorCode.BlackUser) {
        setUser(UserLevel.BLACK);
      } else if (code === CgiErrorCode.UsageExceed) {
        setUser(data?.userLevelType);
      }
      cleanup({ cancelPoll: false, cleanHistories: false });
      return;
    }

    setHistories(pre => [
      {
        urls: Array(AIGC_IMAGE_COUNT).fill({ url: Empty, name: '' }),
        prompt: postData.text,
        status: Status.WAITING,
        relateId: data?.relateId,
        model: postData.model,
        style: postData.style,
      },
      ...pre,
    ]);

    // 开始轮询
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    delayTimerRef.current = setTimeout(() => {
      getImages();
    }, 1000);
  };

  const getImages = () => {
    startPolling();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setTimeout(() => {
      MessagePlugin.error('绘制超时，请点击重试');
      cleanup();
    }, TIMEOUT);
  };

  const { data: imageRes, run: startPolling, cancel: cancelPolling } = useRequest(
    () => getAIResource({
      queryId: '',
      type: AiType.IMAGE,
      page: pagination.page,
      size: pagination.size,
    }),
    {
      pollingInterval: 5000,
      manual: true,
      pollingWhenHidden: false,
    },
  );

  useEffect(() => {
    if (imageRes) setLoading(false);
    const { code, data } = imageRes as CgiResp<GetAIResourceRsp> || { code: 0, data: {} };
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error(FAILED_MESSAGE);
      cleanup();
      return;
    }
    if (isEmpty(data) || isEmpty(data.data)) {
      return;
    }

    onPageChange({ ...pagination, total: data.totalCnt || 0 });
    setHistories(data.data);
    const isFinished = data.data.every(e => e.status === Status.FINISHED);
    if (isFinished) {
      cleanup();
    }
  }, [imageRes, cancelPolling]);

  const cleanup = (params?: {
    cancelPoll?: boolean,
    cleanHistories?: boolean,
  }) => {
    const { cancelPoll = true, cleanHistories = true } = params || {};
    if (delayTimerRef.current && cancelPoll) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (cancelPoll) {
      cancelPolling();
    }

    if (cleanHistories) {
      setHistories(pre => pre.map(history => ({
        ...history,
        status: Status.FINISHED,
      })));
    };
  };

  const handleDelete = (id: string) => {
    const newHistories = histories.filter(e => e.relateId !== id);
    setHistories(newHistories);
  };

  const errMsg = useMemo(() => getUsageExceedMessage(AiType.IMAGE, user), [user]);

  return (
    <div className={styles.aigcImageComponent}>
      <div className={styles.left}>
        <div className={styles.controlPanel}>
          <AigcImageInfo
            ref={aigcImageInfoRef}
            data={{
              ...data,
              style: data?.style || storyInfo?.styleInfo?.style || '',
            }}
            getPrompt={getPrompt}
          />
        </div>
        <div className={styles.generateButtonContainer}>
          <Button
            onClick={generate}
            className={styles.geneBtn}
            theme='default'
          >
            <div className={styles.btnText}>
              <Star3 />生成绘画
            </div>
          </Button>
        </div>
      </div>
      <div className={styles.imageDisplay}>
        <Loading
          loading={loading}
          text='加载中...'
          style={{ display: 'flex', flex: 1 }}
        >
          <AigcImageHistory
            histories={histories}
            generateImages={generateImages}
            getSrc={getSrc}
            onDelete={handleDelete}
            isHorizontal={isHorizontal}
            pagination={pagination}
            loading={loading}
          />
        </Loading>
      </div>

      {errMsg && <Dialog
        theme='danger'
        visible={user !== undefined}
        width={400}
        placement='center'
        cancelBtn={false}
        closeBtn={false}
        onConfirm={() => setUser(undefined)}
        header={errMsg.title}
      >
        <div style={{ paddingTop: 10 }}>{errMsg.desc}</div>
      </Dialog>}
    </div>
  );
};

export default AigcImageComponent;
